import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, TrendingUp, TrendingDown, AlertTriangle, MessageSquareText,
  HardHat, Recycle, Users, FileText, Activity, Target, CheckCircle2, Plus,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ExpirationsPanel } from "@/components/ExpirationsPanel";

const PIE_COLORS = ["#0B2545", "#134074", "#10B981", "#F59E0B", "#EF4444", "#64748B"];

function Kpi({ icon: Icon, label, value, sub, tone = "navy", testid }) {
  const tones = {
    navy: "bg-[#0B2545] text-white",
    green: "bg-emerald-500 text-white",
    amber: "bg-amber-500 text-white",
    red: "bg-rose-500 text-white",
    blue: "bg-[#134074] text-white",
  };
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm transition-all hover:border-[#134074]/30" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <p className="mt-4 text-3xl font-extrabold tracking-tight text-[#0B2545]">{value}</p>
      <p className="mt-1 text-sm font-medium text-slate-600">{label}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

function GaugeCard({ label, actual, target, unit = "", testid, onClick }) {
  const ok = actual <= target;
  return (
    <div onClick={onClick} className="cursor-pointer rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm transition-shadow hover:shadow-md" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
          {ok ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
          {ok ? "Dentro da meta" : "Acima da meta"}
        </span>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-3xl font-extrabold text-[#0B2545]">{actual}{unit}</span>
        <span className="text-sm text-slate-400">/ meta {target}{unit}</span>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${ok ? "bg-emerald-500" : "bg-rose-500"}`}
          style={{ width: `${Math.min(100, target ? (actual / target) * 100 : 0)}%` }} />
      </div>
      <p className="mt-2 text-[11px] font-semibold text-[#134074]">Clique para ver o gráfico →</p>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [accOpen, setAccOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rateOpen, setRateOpen] = useState(false);
  const [acc, setAcc] = useState({ date: "", description: "", with_leave: "true", days_lost: 0, location: "" });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { canEdit } = useAuth();

  const loadStats = () => api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  const submitAccident = async () => {
    if (!acc.date) { toast.error("Informe a data do acidente"); return; }
    setSaving(true);
    try {
      await api.post("/safety/register-accident", {
        date: acc.date, description: acc.description, location: acc.location,
        with_leave: acc.with_leave === "true", days_lost: Number(acc.days_lost || 0),
      });
      toast.success("Acidente registrado. O placar foi reiniciado e o período anterior salvo no histórico.");
      setAccOpen(false);
      setAcc({ date: "", description: "", with_leave: "true", days_lost: 0, location: "" });
      loadStats();
    } catch (e) { toast.error("Falha ao registrar acidente"); } finally { setSaving(false); }
  };

  const downloadMonthly = async () => {
    try {
      const res = await api.get(`/report/monthly/pdf?month=${reportMonth}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Relatorio_SSMA_${reportMonth}.pdf`; a.click();
      URL.revokeObjectURL(url);
      setReportOpen(false);
    } catch { toast.error("Falha ao gerar relatório"); }
  };

  if (!stats) return <div className="py-20 text-center text-slate-400">Carregando indicadores...</div>;

  return (
    <div data-testid="dashboard-page">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0B2545] sm:text-3xl">Dashboard Executivo SSMA</h1>
          <p className="mt-0.5 text-sm text-slate-500">Painel de Segurança, Saúde e Meio Ambiente — Valbrac</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setReportOpen(true)} className="border-[#134074]/30 text-[#134074] hover:bg-[#134074]/5" data-testid="monthly-report-btn">
            <FileText className="mr-1.5 h-4 w-4" /> Relatório do mês
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate("/ocorrencias")} className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="quick-incident">
            <AlertTriangle className="mr-1.5 h-4 w-4" /> Reportar Ocorrência
          </Button>
          <Button size="sm" onClick={() => navigate("/dds")} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="quick-dds">
            <Plus className="mr-1.5 h-4 w-4" /> Novo DDS
          </Button>
        </div>
      </div>

      {/* Safety scoreboard */}
      <div className="mb-6 overflow-hidden rounded-2xl border border-[#E2E8F0] bg-gradient-to-br from-[#0B2545] to-[#134074] p-6 text-white shadow-md sm:p-8" data-testid="safety-scoreboard">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
          <div>
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-emerald-300">
                <ShieldCheck className="h-3.5 w-3.5" /> Placar de Segurança
              </div>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={() => setAccOpen(true)}
                  className="h-7 border-rose-300/40 bg-rose-500/20 px-2 text-xs text-white hover:bg-rose-500/30" data-testid="register-accident-btn">
                  <AlertTriangle className="mr-1 h-3.5 w-3.5" /> Registrar acidente
                </Button>
              )}
            </div>
            <p className="mt-3 text-6xl font-black tracking-tight sm:text-7xl">{stats.days_without_accidents}</p>
            <p className="text-lg font-medium text-slate-200">dias sem acidentes com afastamento</p>
            <p className="mt-1 text-xs text-slate-300">
              Recorde: <b className="text-emerald-300">{stats.best_record}</b> dias
              {stats.last_accident_date ? ` · Último acidente: ${stats.last_accident_date}` : " · Nenhum acidente registrado"}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 sm:gap-6">
            <div className="text-center">
              <p className="text-3xl font-extrabold">{stats.total_employees}</p>
              <p className="text-xs text-slate-300">Colaboradores</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-extrabold">{stats.total_incidents}</p>
              <p className="text-xs text-slate-300">Ocorrências</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-extrabold">{stats.action_close_rate}%</p>
              <p className="text-xs text-slate-300">Ações fechadas</p>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="sm:max-w-[420px]" data-testid="report-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">Relatório mensal de SSMA</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Selecione a competência (mês/ano) para gerar o PDF pronto para auditoria e envio ao cliente.</p>
          <div>
            <Label className="mb-1 block text-xs font-semibold text-slate-600">Competência</Label>
            <Input type="month" value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} data-testid="report-month" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)}>Cancelar</Button>
            <Button onClick={downloadMonthly} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="report-generate"><FileText className="mr-1.5 h-4 w-4" /> Gerar PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={accOpen} onOpenChange={setAccOpen}>        <DialogContent className="sm:max-w-[520px]" data-testid="accident-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">Registrar acidente — reinicia o placar</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Ao registrar, o contador de dias sem acidentes zera e o período anterior é salvo no histórico. Uma ocorrência também é criada automaticamente.</p>
          <div className="grid grid-cols-1 gap-3 py-1 sm:grid-cols-2">
            <div>
              <Label className="mb-1 block text-xs font-semibold text-slate-600">Data do acidente *</Label>
              <Input type="date" value={acc.date} onChange={(e) => setAcc((p) => ({ ...p, date: e.target.value }))} data-testid="accident-date" />
            </div>
            <div>
              <Label className="mb-1 block text-xs font-semibold text-slate-600">Com afastamento?</Label>
              <Select value={acc.with_leave} onValueChange={(v) => setAcc((p) => ({ ...p, with_leave: v }))}>
                <SelectTrigger data-testid="accident-leave"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="true">Sim (com afastamento)</SelectItem><SelectItem value="false">Não (sem afastamento)</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs font-semibold text-slate-600">Dias perdidos</Label>
              <Input type="number" value={acc.days_lost} onChange={(e) => setAcc((p) => ({ ...p, days_lost: e.target.value }))} data-testid="accident-days" />
            </div>
            <div>
              <Label className="mb-1 block text-xs font-semibold text-slate-600">Local</Label>
              <Input value={acc.location} onChange={(e) => setAcc((p) => ({ ...p, location: e.target.value }))} data-testid="accident-location" />
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-1 block text-xs font-semibold text-slate-600">Descrição</Label>
              <Textarea rows={2} value={acc.description} onChange={(e) => setAcc((p) => ({ ...p, description: e.target.value }))} data-testid="accident-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccOpen(false)}>Cancelar</Button>
            <Button onClick={submitAccident} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="accident-submit">{saving ? "Registrando..." : "Registrar e reiniciar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TF / TG gauges */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <GaugeCard label="Taxa de Frequência (TF)" actual={stats.tf_actual} target={stats.tf_target} testid="gauge-tf" onClick={() => setRateOpen(true)} />
        <GaugeCard label="Taxa de Gravidade (TG)" actual={stats.tg_actual} target={stats.tg_target} testid="gauge-tg" onClick={() => setRateOpen(true)} />
      </div>

      <Dialog open={rateOpen} onOpenChange={setRateOpen}>
        <DialogContent className="sm:max-w-[640px]" data-testid="rate-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">Taxas de Frequência (TF) e Gravidade (TG) — NBR 14280</DialogTitle></DialogHeader>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[{ name: "TF (Frequência)", Atual: stats.tf_actual, Meta: stats.tf_target }, { name: "TG (Gravidade)", Atual: stats.tg_actual, Meta: stats.tg_target }]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="Atual" fill="#134074" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Meta" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-lg bg-slate-50 p-3"><p className="text-2xl font-bold text-[#0B2545]">{stats.worked_hours?.toLocaleString?.() || stats.worked_hours}</p><p className="text-xs text-slate-500">Horas trabalhadas</p></div>
            <div className="rounded-lg bg-slate-50 p-3"><p className="text-2xl font-bold text-[#0B2545]">{stats.accidents_with_leave}</p><p className="text-xs text-slate-500">Acidentes c/ afastamento</p></div>
            <div className="rounded-lg bg-slate-50 p-3"><p className="text-2xl font-bold text-[#0B2545]">{stats.lost_days}</p><p className="text-xs text-slate-500">Dias perdidos</p></div>
          </div>
          <p className="text-xs text-slate-400">TF = (acidentes c/ afastamento × 10⁶) / horas trabalhadas · TG = (dias perdidos × 10⁶) / horas trabalhadas. Ajuste os valores em Configurações.</p>
        </DialogContent>
      </Dialog>

      {/* KPI cards */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={MessageSquareText} label="DDS realizados no mês" value={`${stats.dds_month}/${stats.dds_planned}`} sub="Realizados vs. programados" tone="blue" testid="kpi-dds" />
        <Kpi icon={AlertTriangle} label="Quase-acidentes no mês" value={stats.near_miss_month} sub={`${stats.open_actions} ações abertas`} tone="amber" testid="kpi-nearmiss" />
        <Kpi icon={HardHat} label="EPIs com CA vencido" value={stats.ppe_expired} sub={`${stats.ppe_soon} a vencer em 30 dias`} tone={stats.ppe_expired > 0 ? "red" : "green"} testid="kpi-ppe" />
        <Kpi icon={Recycle} label="Resíduos destinados (mês)" value={`${stats.waste_kg_month} kg`} sub={`${stats.mtr_month} MTRs emitidos`} tone="green" testid="kpi-waste" />
      </div>

      <ExpirationsPanel />

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm lg:col-span-2" data-testid="chart-incidents">
          <div className="mb-4 flex items-center gap-2">
            <Activity className="h-4 w-4 text-[#134074]" />
            <h3 className="text-base font-semibold text-[#134074]">Evolução mensal de ocorrências</h3>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={stats.incident_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 12, fill: "#64748B" }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="desvios" name="Desvios" stroke="#F59E0B" strokeWidth={2} />
              <Line type="monotone" dataKey="quase" name="Quase-acidentes" stroke="#134074" strokeWidth={2} />
              <Line type="monotone" dataKey="acidentes" name="Acidentes" stroke="#EF4444" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-[#E2E8F0] bg-white p-5 shadow-sm" data-testid="chart-waste">
          <div className="mb-4 flex items-center gap-2">
            <Recycle className="h-4 w-4 text-emerald-600" />
            <h3 className="text-base font-semibold text-[#134074]">Resíduos por classe (kg)</h3>
          </div>
          {stats.waste_chart.length === 0 ? (
            <p className="py-20 text-center text-sm text-slate-400">Sem dados de resíduos</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={stats.waste_chart} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={80} label>
                  {stats.waste_chart.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Document compliance */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi icon={CheckCircle2} label="Documentos válidos" value={stats.doc_compliance.valid} tone="green" testid="kpi-doc-valid" />
        <Kpi icon={FileText} label="Documentos em revisão" value={stats.doc_compliance.review} tone="amber" testid="kpi-doc-review" />
        <Kpi icon={Target} label="Documentos vencidos" value={stats.doc_compliance.expired} tone={stats.doc_compliance.expired > 0 ? "red" : "green"} testid="kpi-doc-expired" />
      </div>
    </div>
  );
}
